import { IUserReviewItem, IReviewResponse } from "~/mymaps2/reviews/interfaces";
import MyReview from "~/mymaps2/reviews/myreview";
import Header from "~/ui/header";
import createHeaderButton from "~/ui/headerbutton";
import { CLOSE_16, USER_16, CHEVRON_LEFT_16 } from "@sznds/icons";
import Content from "./content";
import SortBox from "~/ui/sortbox";
import ReviewMotivation from "~/ui/review/motivation";
import { REVIEWS_PER_PAGE, REVIEWS_PER_PAGE_FIRST_LOAD, SCROLL_TRESHOLD, SORT_OPTIONS } from "~/mymaps2/reviews/constants";
import { fragment, node, isInViewport, icon, text } from "~/util/dom";
import { createVerifiedUserTooltip } from "~/util/userweb";
import { isTwin } from "~/util/review";
import { getUserReviews, REVIEWS_ORDER } from "~/util/providers/review";
import { addListener, removeListeners } from "~/lib/signals";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { fetchUserIcon } from "~/util/dashboard";
import Avatar from "~/ui/avatar";
import { isDetailActive } from "~/util/detail";
import { store } from "~/state/store";
import { getIcon } from "~/util/providers/poiagg";
import { setCoordinates } from "~/state/map/state";

const LOG_SOURCE = "recenze-publicProfile";
const SCROLL_TO = 33;

interface IMarkers {
	[key: string]: any;
}

interface IReviewsOptions {
	userId: string;
	header: Header;
	direct?: boolean;
	backAction?: () => void;
}

type TMarkersData = {[key: string]: { id: string; coords: any; opts: any }};

export default class Reviews extends Content {
	_userId: string;

	_header: Header;

	_offset: number;

	_resultCount: number;

	_loading: boolean;

	_list: HTMLUListElement;

	_layer: any;

	_markers: IMarkers;

	_markersData: TMarkersData;

	_sc: Array<any>;

	_sortBox: SortBox;

	_topBar: HTMLDivElement;

	_orderValue: REVIEWS_ORDER;

	_scrollId: any;

	_reviews: Array<any>;

	_detailMarker: any;

	constructor({
		userId,
		header,
		direct = true,
		backAction,
	}: IReviewsOptions) {
		super();

		this._userId = userId;
		this._header = header;
		this._orderValue = SORT_OPTIONS[0].value;
		this._loading = false;
		this._layer = Scene.createLayerMarker();
		this._markers = {};
		this._markersData = {};
		this._sc = [];
		Mapy.getComponent("layout").getScrollNode().addEventListener("scroll", this, Mapy.Util.scrollOptionsSupport() ? { passive: true } : false);
		this._offset = 0;
		this._resultCount = 0;
		this._container.innerHTML = "";
		this._scrollId = null;
		this._reviews = [];
		this._detailMarker = null;

		if (direct) {
			const closeButton = createHeaderButton({
				icon: CLOSE_16,
				innerHTML: _("close"),
			});

			closeButton.setAttribute("data-log-publicProfile", "1");
			this._header.leftButtons.appendChild(closeButton);
			closeButton.addEventListener("click", () => {
				if (backAction) {
					backAction();
				} else {
					Mapy.getComponent("userProfile").deactivate();
					Mapy.getComponent("history").save();
				}
			});
		}

		const profileButton = createHeaderButton({
			icon: direct ? USER_16 : CHEVRON_LEFT_16,
			innerHTML: _("userProfile.userContributions"),
		});

		profileButton.setAttribute("data-log-publicProfile", "1");
		profileButton.addEventListener("click", () => {
			Mapy.getComponent("userProfile").activate();
			Mapy.getComponent("userProfile").setState({ up: this._userId });
			setTimeout(function delayedHistorySave() {
				Mapy.getComponent("history").save(true);
			}, 0);
		});
		this._header.leftButtons.appendChild(profileButton);

		this._list = node("ul", { className: "userprofile-reviews__list" });
		this._topBar = node("div", { className: "userprofile-reviews__topbar" });
		this._sortBox = new SortBox({
			options: SORT_OPTIONS,
			onSelect: (value: REVIEWS_ORDER, option) => {
				Mapy.getComponent("logger").log("click", {
					text: _(option.title),
					source: LOG_SOURCE,
					section: "reviews",
					publicProfile: "1",
				});

				this._orderValue = value;
				this._offset = 0;
				this._container.innerHTML = "";
				this._list.innerHTML = "";
				this._reviews.length = 0;
				this._container.appendChild(this._topBar);

				this._loadReviews();
			},
			selectedIndex: SORT_OPTIONS.findIndex(option => option.value === this._orderValue),
		});
		this._container.appendChild(this._topBar);

		this._loadReviews();
		Scene.addLayer(this._layer).enable();
	}

	show() {
		super.show();
		this._layer.enable();

		if (!this._sc.length) {
			this._sc = [
				addListener(this, "detail-activated", "_detailActivated"),
				addListener(this, "detail-deactivated", "_detailDeactivated"),
			];
		}

		if (isDetailActive(store.getState())) {
			Mapy.getComponent("detail").setMarkerVisibility(false);
		}
	}

	hide() {
		super.hide();
		this._layer.disable();

		if (this._sc.length) {
			removeListeners(this._sc);
			this._sc = [];
		}

		if (isDetailActive(store.getState())) {
			Mapy.getComponent("detail").setMarkerVisibility(true);
		}
	}

	destroy() {
		super.destroy();

		this._sortBox.destroy();

		removeListeners(this._sc);
		this._sc = [];
		Mapy.getComponent("layout").getScrollNode().removeEventListener("scroll", this, Mapy.Util.scrollOptionsSupport() ? { passive: true } : false);
		this._layer.removeAll();
		this._markers = [];
		this._layer.disable();
		Scene.removeLayer(this._layer);
	}

	async _loadReviews() {
		if (this._loading) {
			return;
		}

		this._loading = true;
		this._topBar.innerHTML = "";
		Mapy.getComponent("loader").start();
		const loader = node("img", { src: "/img/loader.gif", width: 24, height: 24, className: "userprofile-reviews__loader" });

		this._container.appendChild(loader);

		try {
			const response: IReviewResponse = await getUserReviews({
				userPublicId: this._userId,
				offset: this._offset,
				limit: this._offset === 0 ? REVIEWS_PER_PAGE_FIRST_LOAD : REVIEWS_PER_PAGE,
				order: this._orderValue,
			});

			const motivation = new ReviewMotivation({
				count: response?.total || 0,
				label: "userProfile.reviewCount",
			});

			this._topBar.appendChild(motivation.node);
			if (response?.total) {
				this._topBar.appendChild(this._sortBox.node);
			}

			if (response?.user) {
				const frag = fragment();

				frag.appendChild(node("div", { className: "ui-header__userprofile-title" }, response.user.name));

				if (response.user.bank_id) {
					const verifiedIcon = icon("verifieduser24");

					verifiedIcon.classList.add("ui-header__userprofile-icon");
					frag.appendChild(verifiedIcon);
					createVerifiedUserTooltip(verifiedIcon);
				}

				this._header.title = frag;
				this._header.premium = response.user.hasPremium ? "mapy" : "none";

				const headline = node("h4", { className: "bp-userprofile" });

				// nacteni ikony uzivatele
				try {
					const responseIcon = await fetchUserIcon(this._userId);
					const json = await responseIcon.json();

					headline.appendChild(new Avatar({
						src: `${json.data.privateUrl}/64`,
					}).node);

					headline.classList.add("has-avatar");
				} catch (ex) {
					//
				}

				headline.appendChild(text(response.user.name));
				Mapy.getComponent("layout").setBottomPanel(headline);
			}

			if (response?.totals?.ok > 0) {
				this._resultCount = response.totals.ok;

				const reviews = (response?.reviews || []).filter(item => Object.keys(item.description).length > 0);

				this._updateMap(reviews || [], this._offset === 0);

				if (reviews.length) {
					Mapy.getComponent("userProfile").isActive() && Mapy.SEO.set(_("userProfile.seo", { user: response.user.name }));
					response.reviews.forEach((data, index) => {
						if (!data?.description || !Object.keys(data.description).length) {
							return;
						}

						const review = new MyReview({
							data,
							user: response.user,
							index,
							isCurrentUser: false,
							analytics: {
								source: LOG_SOURCE,
								section: "reviews",
								// eslint-disable-next-line camelcase
								rating_layout: "0",
								position: "top",
								publicProfile: "1",
								type: "publicDashboard",
								sourcePoi: data.review_poi.source,
								typeid: data.description.typeId,
								firmTwin: isTwin(data.description) ? 1 : 0,
							},
							onEnter: () => {
								this._showMarker(`${data.description.source}_${data.description.id}`, true);
							},
							onLeave: () => {
								this._showMarker(`${data.description.source}_${data.description.id}`);
							},
						});

						this._reviews.push(review);
						review.index = this._reviews.length;
						this._list.appendChild(review.node);
					});

					if (this._offset === 0) {
						this._container.appendChild(this._list);
					}

					this._reviewsImpress();
				} else {
					throw Error("jsme na konci");
				}
			} else if (response?.statusMessage) {
				throw Error(response.statusMessage);
			} else {
				throw Error("nejsou data");
			}
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.log(ex);
			// nename nic, udelame empty stranku
			if (this._resultCount === 0) {
				if (ex?.statusMessage === "Bad parameter userPublicId") {
					this._container.appendChild(node("p", { className: "userprofile-reviews__emptyText", innerHTML: _("mymap.reviews.errorUserPublicId") }));
				} else {
					this._container.appendChild(node("p", { className: "userprofile-reviews__emptyText", innerHTML: _("mymap.reviews.emptyForeign") }));
					this._container.appendChild(node("img", { className: "userprofile-reviews__emptyImg", src: "/img/mymap/empty-foreignreviews.svg" }));
				}
			}
		} finally {
			Mapy.getComponent("loader").end();
			loader.remove();
			this._loading = false;
		}
	}

	handleEvent() {
		const scrollNode = Mapy.getComponent("layout").getScrollNode() as HTMLDivElement;
		const scrollTop = scrollNode.scrollTop;
		const scrollHeight = scrollNode.scrollHeight;
		const clientHeight = scrollNode.clientHeight;
		const reviewsPerPage = this._offset === 0 ? REVIEWS_PER_PAGE_FIRST_LOAD : REVIEWS_PER_PAGE;

		if (
			!this._loading
			&& scrollTop + SCROLL_TRESHOLD >= scrollHeight - clientHeight
			&& this._resultCount > 0
			&& this._offset + reviewsPerPage < this._resultCount
		) {
			this._offset += reviewsPerPage;

			this._loadReviews();
		}

		if (this._scrollId) {
			clearTimeout(this._scrollId);
			this._scrollId = null;
		}

		this._scrollId = setTimeout(() => {
			this._scrollId = null;

			this._reviewsImpress();
		}, SCROLL_TO);
	}

	_updateMap(reviews: Array<IUserReviewItem>, zoomToReviews?: boolean) {
		const newMarkers: IMarkers = {};
		const newMarkersData: TMarkersData = {};
		const reviewsCoords = [];

		reviews.forEach(review => {
			const id = `${review.description.source}_${review.description.id}`;

			// existuje uz marker? tak jen presuneme do noveho objektu
			if (id in this._markers) {
				newMarkers[id] = this._markers[id];
				newMarkersData[id] = this._markersData[id];

				return;
			}

			// jeste neexistoval, tak ho pridame
			const typeId = review.description.typeId;
			const typeIcon = review.description.icon && "1" in review.description.icon ? review.description.icon["1"] : getIcon(typeId, { scale: 1 });
			const typeIcon2x = review.description.icon && "2" in review.description.icon ? review.description.icon["2"] : getIcon(typeId, { scale: 2 });
			const coords = Scene.coordsFromWGS84(review.description.mark.lon, review.description.mark.lat);
			const opts = {
				type: typeIcon ? Scene.VISUAL.MARKER_WINDY.SMALL : Scene.VISUAL.MARKER_WINDY.UNIVERSAL,
				...typeIcon ? { icon, srcset: `${typeIcon} 1x${typeIcon2x ? `, ${typeIcon2x} 2x` : ""}` } : {},
			};
			const marker = Scene.createMarkerMapyWindy(coords, id, opts);

			marker.addEventListener("click", function markerClickHandler() {
				Mapy.getComponent("detail").setState({
					source: review.description.source,
					id: review.description.id,
					backTitle: _("mymap.switcher.reviews"),
				}).then(() => {
					Mapy.getComponent("history").save();
				}, () => { /**/ });
			});
			newMarkers[id] = marker;
			newMarkersData[id] = {
				coords,
				id,
				opts,
			};
			this._layer.addMarker(marker);
			reviewsCoords.push(coords);
		});

		// co neskoncilo v novem objektu a je ve starem, to odebereme
		for (const id in this._markers) {
			if (!(id in newMarkers)) {
				this._layer.removeMarker(this._markers[id]);
			}
		}

		// zapamatujeme si nove
		this._markers = newMarkers;
		this._markersData = newMarkersData;

		if (zoomToReviews) {
			const data = Scene.computeCenterZoom(reviewsCoords);
			const [longitude, latitude] = data[0].toWGS84();

			store.dispatch(setCoordinates({
				longitude,
				latitude,
				zoom: data[1],
				animate: true,
			}));
		}
	}

	_detailActivated() {
		this._layer.disable();
	}

	_detailDeactivated() {
		this._layer.enable();
	}

	_reviewsImpress() {
		this._reviews.forEach(review => {
			if (isInViewport(review.header)) {
				review.firmImpressAnalytics();
			}
		});
	}

	_showMarker(id: string, isBig?: boolean) {
		const data = this._markersData[id];

		if (!isBig && this._detailMarker) {
			this._layer.removeMarker(this._detailMarker);
			this._detailMarker = null;
		} else if (isBig && !this._detailMarker && data) {
			this._detailMarker = Scene.createMarkerMapyWindy(data.coords, undefined, {
				...data.opts,
				type: Scene.VISUAL.MARKER_WINDY.BIG,
			});
			this._layer.addMarker(this._detailMarker);

			const cont = this._detailMarker.getContainerSync();

			cont.style.zIndex = 1000;
		}
	}
}
